import {
  getNotificationById,
  updateNotification,
} from "../../../Redux/Notifications/NotificationsActions";
import {
  getAllBuildings,
  getMembersByBuildingId,
} from "../../../Redux/Buildings/BuildingActions";
import validator, { ValidationField } from "../../../Util/validator";
import Loading from "../../../Components/Common/Loading/Loading";
import { AppDispatch, RootState } from "../../../Redux/store";
import { Link, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import arrowIcon from "../../../Assets/arrowleft.png";
import { useEffect, useMemo, useState } from "react";
import styles from "./EditNotification.module.scss";
import makeAnimated from "react-select/animated";
import { Switch } from "react-animated-switch";
import Select from "react-select";

const animatedComponents = makeAnimated();

function EditNotification() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const {
    buildings,
    buildingsLoading,
    getMembersByBuildingIdList,
    getMembersByBuildingIdLoading,
  } = useSelector((state: RootState) => state.buildings);
  const {
    updateNotificationLoading,
    notificationByIdLoading,
    notificationByIdData,
  } = useSelector((state: RootState) => state.notifications);

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [isAllMembersSelected, setIsAllMembersSelected] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState<string>("");
  const [selectedMembers, setSelectedMembers] = useState<any[]>([]);

  const [errors, setErrors] = useState<{
    title: string;
    message: string;
    building: string;
    selectedMembers: string;
  }>({
    title: "",
    message: "",
    building: "",
    selectedMembers: "",
  });

  const publishNotificationHandler = () => {
    let validationRules: ValidationField[] = [
      {
        key: "title",
        value: title,
        type: "text",
      },
      {
        key: "message",
        value: message,
        type: "text",
      },
      {
        key: "building",
        value: selectedBuilding,
        type: "text",
      },
    ];

    if (!isAllMembersSelected) {
      validationRules.push({
        key: "selectedMembers",
        value: selectedMembers,
        type: "array",
      });
    }

    const { isValid, errors: fieldErrors } = validator(validationRules);

    setErrors({ ...errors, ...fieldErrors });

    if (isValid && !updateNotificationLoading) {
      const successHandler = () => {
        navigate("/admin/notifications", { replace: true });
      };

      dispatch(
        updateNotification({
          notificationId: Number(id),
          title,
          message,
          sendToAll: isAllMembersSelected,
          buildingId: selectedBuilding,
          memberIds: isAllMembersSelected
            ? []
            : selectedMembers?.map((member: any) => member?.value),
          successHandler,
        }),
      );
    }
  };

  const options: any = useMemo(() => {
    return getMembersByBuildingIdList?.map((member: any) => ({
      label: member?.email,
      value: member?.memberId,
      data: member,
    }));
  }, [getMembersByBuildingIdList]);

  const finalOptions: any = useMemo(() => {
    return options?.filter((option: any) => option?.data?.isActive === true);
  }, [options]);

  useEffect(() => {
    dispatch(getAllBuildings());
    dispatch(getNotificationById({ id: Number(id) }));
  }, [dispatch]);

  useEffect(() => {
    if (notificationByIdData) {
      setTitle(notificationByIdData?.title);
      setMessage(notificationByIdData?.message);
      setIsAllMembersSelected(notificationByIdData?.sendToAll);
      setSelectedBuilding(notificationByIdData?.buildingId);
      dispatch(
        getMembersByBuildingId({
          buildingId: notificationByIdData?.buildingId,
        }),
      );
      const members = notificationByIdData?.members?.map((member: any) => ({
        label: member?.emailAddress,
        value: member?.memberId,
        data: member,
      }));

      setSelectedMembers(members);
    }
  }, [notificationByIdData]);

  if (buildingsLoading || notificationByIdLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.mainContainer}>
      <Link
        to="/admin/notifications"
        replace
        className={styles.backButtonContainer}
      >
        <button className={styles.button}>
          <img src={arrowIcon} alt="arrowIcon" className={styles.arrowIcon} />
          Back to Notifications
        </button>
      </Link>
      <div className={styles.headingContainer}>
        <h1 className={styles.heading}>Publish Notification</h1>
      </div>
      <div className={styles.formContainer}>
        <div className={styles.inputContainer}>
          <p className={styles.label}>Select Building</p>
          <select
            className={styles.input}
            value={selectedBuilding}
            onChange={(e) => {
              setSelectedBuilding(e.target.value);
              dispatch(getMembersByBuildingId({ buildingId: e.target.value }));
              setSelectedMembers([]);
            }}
          >
            {!selectedBuilding && <option value="">Select Building</option>}
            {buildings?.map((building: any, index: number) => (
              <option key={index} value={building?.buildingId}>
                {building?.buildingName}
              </option>
            ))}
          </select>
          <p className={styles.error}>{errors.building}</p>
        </div>

        <div className={styles.inputContainer}>
          <p className={styles.label}>Title</p>
          <input
            type="text"
            className={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <p className={styles.error}>{errors.title}</p>
        </div>

        <div className={styles.inputContainer}>
          <p className={styles.label}>Message</p>
          <textarea
            className={styles.input}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
          />
          <p className={styles.error}>{errors.message}</p>
        </div>

        <div className={styles.informationContainer}>
          <p className={styles.information}>
            Tags you can use: [Email], [Name]
          </p>
        </div>

        <div className={styles.inputContainer}>
          <p className={styles.label}>Select all members?</p>
          <Switch
            checked={isAllMembersSelected}
            onChange={setIsAllMembersSelected}
          />
        </div>

        {!isAllMembersSelected && (
          <div className={styles.inputContainer}>
            <Select
              className={styles.select}
              closeMenuOnSelect={false}
              components={animatedComponents}
              value={selectedMembers}
              isMulti
              options={finalOptions ?? []}
              onChange={(e: any) => {
                setSelectedMembers(e);
              }}
            />
            <p className={styles.error}>{errors.selectedMembers}</p>
          </div>
        )}

        {(updateNotificationLoading || getMembersByBuildingIdLoading) && (
          <Loading />
        )}

        <div className={styles.buttonContainer}>
          <button
            className={styles.button}
            onClick={publishNotificationHandler}
          >
            Update Notification
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditNotification;
